/**
 * useSession.jsx
 * One shared health/identity check for the whole app: who is signed in,
 * whether the API answers, and whether the database is reachable.
 *
 * Screens read it rather than each calling /api/health themselves.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../services/api.js';

const SessionContext = createContext(null);

export function SessionProvider({ children }) {
  const [health, setHealth] = useState(null);
  const [error, setError] = useState(null);
  const [checking, setChecking] = useState(true);

  const refresh = useCallback(async () => {
    setChecking(true);
    try {
      setHealth(await api.health());
      setError(null);
    } catch (e) {
      setHealth(null);
      setError(e);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({
      health,
      error,
      checking,
      refresh,
      user: health?.user ?? null,
      apiUp: !!health?.ok,
      dbOk: health?.db === 'ok',
    }),
    [health, error, checking, refresh],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>');
  return ctx;
}
